/**
 * Reffconnect — Google Analytics 4 event tracking utility
 *
 * HOW TO CONNECT YOUR GA4 PROPERTY
 * ---------------------------------
 * 1. Go to https://analytics.google.com
 * 2. Create a property → choose "Web" → get your Measurement ID (G-XXXXXXXXXX)
 * 3. Open  my-app/public/index.html
 * 4. Replace  G-XXXXXXXXXX  in the two places marked <!-- GA4 -->
 *
 * All events in this file follow GA4 recommended event names where possible:
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const SESSION_ID_STORAGE_KEY = 'reff_analytics_session_id_v1';
const FIRST_VISIT_STORAGE_KEY = 'reff_analytics_first_visit_v1';
const UTM_SNAPSHOT_STORAGE_KEY = 'reff_analytics_utm_snapshot_v1';

type UTMSource = {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
};

function safeGetSessionStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetSessionStorage(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

function safeGetLocalStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetLocalStorage(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
}

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  const existing = safeGetSessionStorage(SESSION_ID_STORAGE_KEY);
  if (existing) return existing;
  const next = generateId();
  safeSetSessionStorage(SESSION_ID_STORAGE_KEY, next);
  return next;
}

function getOrCreateFirstVisitId(): string {
  if (typeof window === 'undefined') return 'server';
  const existing = safeGetLocalStorage(FIRST_VISIT_STORAGE_KEY);
  if (existing) return existing;
  const next = generateId();
  safeSetLocalStorage(FIRST_VISIT_STORAGE_KEY, next);
  return next;
}

function readOrCaptureUTM(): UTMSource {
  if (typeof window === 'undefined') {
    return { utm_source: '', utm_medium: '', utm_campaign: '' };
  }
  const params = new URLSearchParams(window.location.search);
  const fromUrl: UTMSource = {
    utm_source: (params.get('utm_source') || '').slice(0, 80),
    utm_medium: (params.get('utm_medium') || '').slice(0, 80),
    utm_campaign: (params.get('utm_campaign') || '').slice(0, 120),
  };
  if (fromUrl.utm_source || fromUrl.utm_medium || fromUrl.utm_campaign) {
    safeSetLocalStorage(UTM_SNAPSHOT_STORAGE_KEY, JSON.stringify(fromUrl));
    return fromUrl;
  }
  const raw = safeGetLocalStorage(UTM_SNAPSHOT_STORAGE_KEY);
  if (!raw) return { utm_source: '', utm_medium: '', utm_campaign: '' };
  try {
    const parsed = JSON.parse(raw) as Partial<UTMSource>;
    return {
      utm_source: (parsed.utm_source || '').slice(0, 80),
      utm_medium: (parsed.utm_medium || '').slice(0, 80),
      utm_campaign: (parsed.utm_campaign || '').slice(0, 120),
    };
  } catch {
    return { utm_source: '', utm_medium: '', utm_campaign: '' };
  }
}

function getEventContext() {
  if (typeof window === 'undefined') return {};
  return {
    session_id: getOrCreateSessionId(),
    first_visit_id: getOrCreateFirstVisitId(),
    ...readOrCaptureUTM(),
    device_type: getDeviceType(),
    page_path: window.location.pathname,
  };
}

/** Safe wrapper — silently does nothing if GA4 isn't loaded yet */
function gtag(...args: unknown[]) {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    if (args[0] === 'event' && typeof args[1] === 'string') {
      const payload = (args[2] && typeof args[2] === 'object' ? (args[2] as Record<string, unknown>) : {}) || {};
      window.gtag('event', args[1], { ...getEventContext(), ...payload });
      return;
    }
    window.gtag(...args);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Page tracking
// ─────────────────────────────────────────────────────────────────────────────

/** Fire a page_view hit — call this on every route change */
export function trackPageView(path: string, title?: string) {
  gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

export function getDeviceType(width = typeof window !== 'undefined' ? window.innerWidth : 1366): DeviceType {
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/** Enriched page-view for custom dashboards (device + viewport split). */
export function trackPageViewEnriched(path: string, title?: string) {
  if (typeof window === 'undefined') return;
  gtag('event', 'page_view_enriched', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
    device_type: getDeviceType(),
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
  });
}

export function trackSessionStartCustom() {
  gtag('event', 'session_start_custom', {
    source: 'global_observer',
  });
}

export function trackFirstVisitCustom() {
  gtag('event', 'first_visit_custom', {
    source: 'global_observer',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Authentication events
// ─────────────────────────────────────────────────────────────────────────────

/** User successfully logged in */
export function trackLogin(method: 'email' | 'google' = 'email') {
  gtag('event', 'login', { method });
}

/** Login attempt failed */
export function trackLoginFailed(reason: string) {
  gtag('event', 'login_failed', { reason });
}

/** User successfully created an account */
export function trackSignUp(method: 'email' | 'google' = 'email') {
  gtag('event', 'sign_up', { method });
}

/** Sign-up attempt failed */
export function trackSignUpFailed(reason: string) {
  gtag('event', 'sign_up_failed', { reason });
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero / landing CTA events
// ─────────────────────────────────────────────────────────────────────────────

/** Any CTA click on the landing / hero section */
export function trackCTAClick(label: string, destination?: string) {
  gtag('event', 'cta_click', {
    cta_label: label,
    destination: destination ?? '',
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Referral / job events
// ─────────────────────────────────────────────────────────────────────────────

/** Job seeker sent a referral request */
export function trackReferralRequestSent(params?: {
  jobTitle?: string;
  company?: string;
  priority?: string;
}) {
  gtag('event', 'referral_request_sent', {
    job_title: params?.jobTitle ?? '',
    company: params?.company ?? '',
    priority: params?.priority ?? '',
  });
}

/** Referrer accepted / completed a referral */
export function trackReferralCompleted(params?: {
  jobTitle?: string;
  company?: string;
}) {
  gtag('event', 'referral_completed', {
    job_title: params?.jobTitle ?? '',
    company: params?.company ?? '',
  });
}

/** A new job opening was posted */
export function trackJobPosted(params?: {
  jobTitle?: string;
  company?: string;
  jobType?: string;
}) {
  gtag('event', 'job_posted', {
    job_title: params?.jobTitle ?? '',
    company: params?.company ?? '',
    job_type: params?.jobType ?? '',
  });
}

/** User copied a job URL or Job ID */
export function trackCopy(label: 'job_url' | 'job_id' | string) {
  gtag('event', 'copy', { copy_target: label });
}

// ─────────────────────────────────────────────────────────────────────────────
// Navigation / engagement events
// ─────────────────────────────────────────────────────────────────────────────

/** User opened the sidebar */
export function trackSidebarOpen() {
  gtag('event', 'sidebar_open');
}

/** User clicked a sidebar nav link */
export function trackSidebarNav(destination: string) {
  gtag('event', 'sidebar_nav', { destination });
}

/** User clicked a landing page nav link */
export function trackNavClick(label: string, destination: string, menuType?: 'desktop_nav' | 'mobile_nav' | 'footer_nav') {
  gtag('event', 'nav_click', { nav_label: label, destination, menu_type: menuType ?? '' });
}

/** User opened/closed an FAQ item */
export function trackFAQExpand(question: string, action: 'open' | 'close') {
  gtag('event', 'faq_expand', { faq_question: question, action });
}

/** User expanded a "How It Works" accordion step */
export function trackHowItWorksStep(stepNumber: number, stepTitle: string) {
  gtag('event', 'how_it_works_step', {
    step_number: stepNumber,
    step_title: stepTitle,
  });
}

/** User clicked a footer navigation link */
export function trackFooterLinkClick(label: string, destination?: string) {
  gtag('event', 'footer_link_click', {
    link_label: label,
    destination: destination ?? '',
  });
}

/** User clicked a social media icon */
export function trackSocialClick(platform: string) {
  gtag('event', 'social_click', { platform });
}

/** User clicked a blog article */
export function trackBlogRead(title: string, slug: string) {
  gtag('event', 'blog_read', { blog_title: title, blog_slug: slug });
}

/** User submitted the contact form */
export function trackContactFormSubmit() {
  gtag('event', 'contact_form_submit');
}

// ─────────────────────────────────────────────────────────────────────────────
// Pre-launch waitlist modal (Header)
// ─────────────────────────────────────────────────────────────────────────────

const pagePath = () => (typeof window !== 'undefined' ? window.location.pathname : '');

/** Waitlist / notify-me dialog opened (Sign In or Sign Up variant). */
export function trackWaitlistModalOpen(params: { variant: 'signin' | 'signup' }) {
  gtag('event', 'waitlist_modal_open', {
    modal_variant: params.variant,
    page_path: pagePath(),
  });
}

/** User clicked the primary submit control (Notify me / Join waitlist). */
export function trackWaitlistNotifyButtonClick(params: {
  variant: 'signin' | 'signup';
  submit_label: string;
}) {
  gtag('event', 'waitlist_notify_click', {
    modal_variant: params.variant,
    submit_label: params.submit_label,
    page_path: pagePath(),
  });
}

/** Client-side validation failed before API call. */
export function trackWaitlistFormInvalid(params: {
  variant: 'signin' | 'signup';
  field: 'email' | 'role' | 'company';
}) {
  gtag('event', 'waitlist_form_invalid', {
    modal_variant: params.variant,
    invalid_field: params.field,
    page_path: pagePath(),
  });
}

/** Waitlist entry saved successfully (after API success). */
export function trackWaitlistNotifySubmit(params: {
  variant: 'signin' | 'signup';
  audience_role: 'working_professional' | 'fresher';
  has_company: boolean;
}) {
  gtag('event', 'waitlist_notify_submit', {
    modal_variant: params.variant,
    audience_role: params.audience_role,
    has_company: params.has_company,
    page_path: pagePath(),
  });
}

/** Waitlist API or network error after submit. */
export function trackWaitlistSubmitFailed(params: {
  variant: 'signin' | 'signup';
  error_message: string;
}) {
  gtag('event', 'waitlist_submit_failed', {
    modal_variant: params.variant,
    error_message: params.error_message.slice(0, 120),
    page_path: pagePath(),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Services page (/services)
// ─────────────────────────────────────────────────────────────────────────────

/** CTA or link click on the services page (use with delegated listener + data-analytics-section). */
export function trackServicesClick(params: {
  section: string;
  element_tag: string;
  label: string;
  href: string;
}) {
  gtag('event', 'services_click', {
    section: params.section,
    element_tag: params.element_tag,
    label: params.label,
    href: params.href,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '/services',
  });
  gtag('event', 'select_content', {
    content_type: 'services_cta',
    item_id: `${params.section}:${params.label}`.slice(0, 100),
  });
}

/** Non-navigation engagement on the services page (tabs, FAQ carousel, workflow audience, etc.). */
export function trackServicesEngagement(params: {
  action: 'tab' | 'faq_nav' | 'process_audience' | 'carousel_dot' | 'process_carousel';
  section: string;
  detail: string;
}) {
  gtag('event', 'services_engagement', {
    engagement_action: params.action,
    section: params.section,
    detail: params.detail,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '/services',
  });
}

/** Generic custom event — for anything not covered above */
export function trackEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>
) {
  gtag('event', eventName, params ?? {});
}

export function trackGlobalButtonClick(params: {
  label: string;
  button_id: string;
  page_path: string;
  section: string;
  element_tag: string;
}) {
  gtag('event', 'button_click', {
    button_label: params.label,
    button_id: params.button_id,
    page_path: params.page_path,
    section: params.section,
    element_tag: params.element_tag,
    device_type: getDeviceType(),
  });
}

export function trackGlobalLinkClick(params: {
  label: string;
  page_path: string;
  section: string;
  destination: string;
}) {
  gtag('event', 'link_click', {
    link_label: params.label,
    page_path: params.page_path,
    section: params.section,
    destination: params.destination,
    device_type: getDeviceType(),
  });
}

export function trackScrollDepth(params: { page_path: string; percent: 25 | 50 | 75 | 100 }) {
  gtag('event', 'scroll_depth', {
    page_path: params.page_path,
    percent_scrolled: params.percent,
    device_type: getDeviceType(),
  });
}

export function trackSectionView(params: { page_path: string; section: string }) {
  gtag('event', 'section_view', {
    page_path: params.page_path,
    section: params.section,
    device_type: getDeviceType(),
  });
}

export function trackTimeOnPage(params: { page_path: string; seconds: 30 | 60 }) {
  gtag('event', params.seconds === 30 ? 'time_on_page_30s' : 'time_on_page_60s', {
    page_path: params.page_path,
  });
}

export function trackFormStart(params: { form_name: string; page_path: string }) {
  gtag('event', 'form_start', params);
}

export function trackFormFieldBlur(params: { form_name: string; field_name: string; page_path: string }) {
  gtag('event', 'form_field_blur', params);
}

export function trackFormValidationError(params: {
  form_name: string;
  field_name: string;
  message: string;
  page_path: string;
}) {
  gtag('event', 'form_validation_error', { ...params, message: params.message.slice(0, 120) });
}

export function trackFormSubmitAttempt(params: { form_name: string; page_path: string }) {
  gtag('event', 'form_submit_attempt', params);
}

export function trackFormSubmitSuccess(params: { form_name: string; page_path: string }) {
  gtag('event', 'form_submit_success', params);
}

export function trackFormSubmitFailure(params: { form_name: string; message: string; page_path: string }) {
  gtag('event', 'form_submit_failure', { ...params, message: params.message.slice(0, 120) });
}

export function trackModalClose(params: { modal_name: string; page_path: string }) {
  gtag('event', 'modal_close', params);
}
